const SCALAR_LINKS = ['fatherId', 'motherId'];
const ARRAY_LINKS = [
  'spouseIds',
  'childrenIds',
  'siblingIds',
  'parentIds',
  'crossLevelSpouseIds'
];

const isProband = (node) => !!node?.proBandId || node?.pid === 1;

const isRealMember = (node) =>
  !!node &&
  !node.deleted &&
  !node.hidden &&
  !isProband(node) &&
  (!!node.familyMemberId || !!node.upn);

const addEdge = (adj, a, b) => {
  if (a === undefined || a === null || a === '') return;
  if (b === undefined || b === null || b === '') return;
  const ka = String(a);
  const kb = String(b);
  if (ka === kb) return;
  if (!adj.has(ka)) adj.set(ka, new Set());
  if (!adj.has(kb)) adj.set(kb, new Set());
  adj.get(ka).add(kb);
  adj.get(kb).add(ka);
};

export const buildAdjacency = (members) => {
  const adj = new Map();
  (members || []).forEach((node) => {
    if (!node || node.pid === undefined || node.pid === null) return;
    if (node.deleted) return;
    const pid = String(node.pid);
    if (!adj.has(pid)) adj.set(pid, new Set());
    SCALAR_LINKS.forEach((f) => addEdge(adj, pid, node[f]));
    ARRAY_LINKS.forEach((f) => {
      if (Array.isArray(node[f])) node[f].forEach((v) => addEdge(adj, pid, v));
    });
  });
  return adj;
};

const bfs = (adj, seeds) => {
  const seen = new Set();
  const stack = seeds.map((p) => String(p));
  while (stack.length) {
    const cur = stack.pop();
    if (seen.has(cur)) continue;
    seen.add(cur);
    const neighbours = adj.get(cur);
    if (neighbours) neighbours.forEach((n) => !seen.has(n) && stack.push(n));
  }
  return seen;
};

// The proband's connected component (pid strings).
export const getProbandComponent = (members) => {
  const proband = (members || []).find((n) => isProband(n));
  if (!proband) return new Set();
  return bfs(buildAdjacency(members), [proband.pid]);
};

// pid strings of members eligible to migrate: real, non-proband, and not in
// the proband's connected component.
export const getEligiblePids = (members) => {
  const probandComponent = getProbandComponent(members);
  const eligible = new Set();
  (members || []).forEach((node) => {
    if (!isRealMember(node)) return;
    if (probandComponent.has(String(node.pid))) return;
    eligible.add(String(node.pid));
  });
  return eligible;
};

// Full connected component containing `pid` (pid strings).
export const getComponentOf = (members, pid) =>
  bfs(buildAdjacency(members), [pid]);

// A selection is fully independent when its closure equals the selection.
export const isFullyIndependentSelection = (members, selectedPidSet) => {
  const selected = new Set([...selectedPidSet].map((p) => String(p)));
  if (selected.size === 0) return { independent: false, danglingPids: [] };
  const closure = bfs(buildAdjacency(members), [...selected]);
  const danglingPids = [...closure].filter((p) => !selected.has(p));
  return { independent: danglingPids.length === 0, danglingPids };
};

export const getMigratablePidsDetail = (members, selectedPidSet) => {
  const eligible = getEligiblePids(members);
  const selected = new Set([...selectedPidSet].map((p) => String(p)));
  const allEligible =
    selected.size > 0 && [...selected].every((p) => eligible.has(p));
  const { independent, danglingPids } = isFullyIndependentSelection(
    members,
    selected
  );
  return {
    eligible,
    allEligible,
    independent: independent && allEligible,
    danglingPids
  };
};
